import { Calendar, ExternalLink, Filter, Rss, Search } from 'lucide-react';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { LoadingCard, LoadingState } from '../components/ui/loading';
import { TagsSidebar } from '../components/TagsSidebar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useProgressiveNews } from '../hooks/useProgressiveContent';
import { formatDate, getRelativeTime, getNewsUrl } from '../lib/content';
import { topicLabels } from '../lib/router';

// Analytics tracking function
const trackExternalLink = (url: string, source: string) => {
  // Google Analytics 4 event tracking
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', 'external_link_click', {
      destination_url: url,
      source_location: source,
      link_domain: new URL(url).hostname
    });
  }
};

export function NewsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTopic, setSelectedTopic] = useState<string>('all');
  const [selectedSource, setSelectedSource] = useState<string>('all');
  
  const { posts, loading, error } = useProgressiveNews();

  // Fallback to simple filtering for now
  const filteredPosts = posts
    .filter(post => {
      const matchesSearch = searchQuery === '' || 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.dek.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesTopic = selectedTopic === 'all' || post.topics.includes(selectedTopic);
      
      const matchesSource = selectedSource === 'all' || post.source_name === selectedSource;
      
      return matchesSearch && matchesTopic && matchesSource;
    })
    .sort((a, b) => new Date(b.published_at).getTime() - new Date(a.published_at).getTime());

  // Get unique sources and topics for filters
  const sources = Array.from(new Set(posts.map(post => post.source_name)));
  const topics = Array.from(new Set(posts.flatMap(post => post.topics)));

  if (error) {
    return (
      <div className="container py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">Unable to load news</h1>
        <p className="text-muted-foreground">{error}</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>AI Literacy News - Daily Updates | Promptliteracy.ai</title>
        <meta name="description" content="Stay updated with daily curated news about AI literacy, prompt engineering, educational technology, and responsible AI developments. Expert analysis and insights." />
        
        {/* Open Graph */}
        <meta property="og:title" content="AI Literacy News - Daily Updates | Promptliteracy.ai" />
        <meta property="og:description" content="Stay updated with daily curated news about AI literacy, prompt engineering, educational technology, and responsible AI developments." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://promptliteracy.ai/news" />
        <meta property="og:site_name" content="Promptliteracy.ai" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="AI Literacy News - Daily Updates" />
        <meta name="twitter:description" content="Daily curated news about AI literacy, prompt engineering, and educational technology developments." />
        
        {/* Additional SEO */}
        <meta name="keywords" content="AI news, prompt engineering news, AI literacy updates, artificial intelligence education news, AI policy, educational technology news" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://promptliteracy.ai/news" />
        
        {/* RSS Feed */}
        <link rel="alternate" type="application/rss+xml" title="Promptliteracy.ai News Feed" href="/feed.xml" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "AI Literacy News",
            "description": "Daily curated news about AI literacy, prompt engineering, and educational technology",
            "url": "https://promptliteracy.ai/news",
            "mainEntity": {
              "@type": "ItemList",
              "name": "AI Literacy News Articles"
            }
          })}
        </script>
      </Helmet>

      {/* Header Section */}
      <section className="py-16 bg-primary text-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Rss className="h-8 w-8" />
              <h1 className="text-4xl font-bold">AI Literacy News</h1>
            </div>
            <p className="text-xl opacity-90 mb-8">
              Daily curated news about AI literacy, prompt engineering, and educational technology developments
            </p>
            
            <div className="flex items-center justify-center gap-6 text-sm opacity-90">
              <div className="flex items-center gap-2">
                <Rss className="h-4 w-4" />
                <span>Updated daily</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>Curated by experts</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-primary/5 relative">
        <div className="container">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Filters */}
              <div className="mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold">Browse Articles</h2>
              <p className="text-muted-foreground mt-2">
                Find exactly what you're looking for
              </p>
            </div>
            <Button variant="outline" size="sm" asChild className="border-slate-300 btn-blue-hover transition-colors whitespace-nowrap">
              <a href="/feed.xml" target="_blank" rel="noopener noreferrer">
                <Rss className="mr-2 h-4 w-4" />
                RSS Feed
              </a>
            </Button>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search news articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Select value={selectedTopic} onValueChange={setSelectedTopic}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="All Topics" />
                </SelectTrigger>
                <SelectContent className="z-[9999] bg-white border border-border shadow-lg">
                  <SelectItem value="all" className="link-underline">All Topics</SelectItem>
                  {topics.filter(topic => topic && topic.trim() !== '').map((topic) => (
                    <SelectItem key={topic} value={topic} className="link-underline">
                      {topicLabels[topic] || topic.replace('-', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedSource} onValueChange={setSelectedSource}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent className="z-[9999] bg-white border border-border shadow-lg">
                  <SelectItem value="all" className="link-underline">All Sources</SelectItem>
                  {sources.filter(source => source && source.trim() !== '').map((source) => (
                    <SelectItem key={source} value={source} className="link-underline">
                      {source}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results count */}
          <div className="mt-4 text-sm text-muted-foreground">
            {loading ? <LoadingState message={"Loading..."} />  : `${filteredPosts.length} article${filteredPosts.length !== 1 ? 's' : ''} found`}
          </div>
        </div>

        {/* News Grid */}
        {loading ? (
          <div className="grid gap-6">
            {[...Array(6)].map((_, i) => (
              <LoadingCard key={i} className="border-l-4 border-l-muted" />
            ))}
          </div>
        ) : filteredPosts.length > 0 ? (
          <div className="grid gap-6">
            {filteredPosts.map((post) => (
              <Link key={post.slug} to={getNewsUrl(post)} className="block group">
                <Card className="card-slate hover:shadow-lg transition-all hover:scale-[1.01] border-l-4 border-l-primary/30 hover:border-l-primary cursor-pointer card-accent">
                  <CardHeader>
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <CardTitle className="text-xl mb-2 leading-tight text-primary">
                          {post.title}
                        </CardTitle>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1 px-2 py-1 bg-primary/10 rounded-full">
                            <span className="font-medium text-primary">{post.source_name}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3 text-primary" />
                          <span>{formatDate(post.published_at)}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground/60 ml-4">
                          <span>{getRelativeTime(post.published_at)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={post.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg bg-primary/10 text-primary hover:bg-primary hover:text-white transition-colors"
                        title="Read original article"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          trackExternalLink(post.source_url, `news_page_icon_${post.source_name}`);
                        }}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">
                    {post.dek}
                  </p>
                  
                  {post.curator_note && (
                    <div className="bg-secondary/10 border-l-4 border-secondary pl-4 py-3 my-4 rounded-r-lg">
                      <p className="text-sm">
                        <span className="font-medium text-secondary">Editor's note:</span> 
                        <span className="text-muted-foreground italic ml-1">{post.curator_note}</span>
                      </p>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-1.5 sm:gap-2">
                    {post.topics.map((topic) => (
                      <Link 
                        key={topic}
                        to={`/tag/${topic}`}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        className="inline-block px-2 py-1 text-xs bg-secondary/10 text-secondary hover:bg-secondary/20 hover:text-primary rounded transition-colors border border-secondary/20 whitespace-nowrap"
                      >
                        {topicLabels[topic] || topic.replace('-', ' ')}
                      </Link>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-primary/20">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="border-slate-300 btn-blue-hover transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        trackExternalLink(post.source_url, `news_page_button_${post.source_name}`);
                        window.open(post.source_url, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      Read Original Article
                      <ExternalLink className="ml-2 h-3 w-3" />
                    </Button>
                    
                    <div className="text-xs text-muted-foreground">
                      <span>Published {getRelativeTime(post.published_at)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No articles found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search terms or filters
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery('');
                setSelectedTopic('all');
                setSelectedSource('all');
              }}
              className="border-slate-300 btn-blue-hover transition-colors"
            >
              Clear Filters
            </Button>
          </div>
        )}

        {/* Pagination would go here in a real implementation */}
        {filteredPosts.length > 12 && (
          <div className="mt-12 text-center">
            <Button variant="outline" className="border-slate-300 btn-blue-hover transition-colors">
              Load More Articles
            </Button>
          </div>
        )}
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-1">
          <TagsSidebar
            tags={[
              ...topics.map(topic => ({ tag: topic, count: posts.filter(p => p.topics.includes(topic)).length, type: 'topic' as const })),
              ...sources.map(source => ({ tag: source, count: posts.filter(p => p.source_name === source).length, type: 'audience' as const }))
            ]}
            title="Popular Tags"
            maxTags={12}
          />
        </aside>
          </div>
        </div>
      </section>
    </>
  );
}